import { ref } from 'vue';

/**
 * useTutorialManager
 * ------------------
 * Decides WHEN a Driver.js tutorial should be shown and persists that state in
 * localStorage, scoped per user. It never runs Driver.js itself: components
 * keep launching their own tours and only ask this composable for permission.
 *
 * State is stored under `tutorial-manager:${userId}` so different users on the
 * same computer never share tutorial state.
 *
 * Shape persisted in localStorage:
 * {
 *   userId,
 *   lastSessionAt,      // start of the current session
 *   previousSessionAt,  // start of the session before this one (inactivity checks)
 *   tutorials: { [tutorialId]: { hasBeenShown, lastShownAt } },
 *   actions:   { [actionKey]:  { lastUsedAt, lastTutorialShownAt } },
 * }
 */

/** Default window (in days) after which a tutorial may be shown again. */
export const DEFAULT_REPEAT_AFTER_DAYS = 2;

const STORAGE_KEY_PREFIX = 'tutorial-manager';
const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Module-level state: every component calling useTutorialManager() shares the
// same active user session, so initUserTutorialSession() only needs to run
// once per page load (e.g. from app.js when the authenticated user is known).
const activeUserId = ref(null);
const wasInactiveOnSessionStart = ref(false);

let hasWarnedMissingUser = false;

/* -------------------------------------------------------------------------- */
/* Internal helpers                                                           */
/* -------------------------------------------------------------------------- */

/** localStorage can be missing (SSR) or throw (private browsing, locked contexts). */
const isStorageAvailable = () => {
    if (typeof window === 'undefined') {
        return false;
    }

    try {
        const probeKey = `${STORAGE_KEY_PREFIX}:__probe__`;
        window.localStorage.setItem(probeKey, '1');
        window.localStorage.removeItem(probeKey);
        return true;
    } catch {
        return false;
    }
};

const getStorageKey = (userId) => `${STORAGE_KEY_PREFIX}:${userId}`;

const getNowISO = () => new Date().toISOString();

/** JSON.parse that never throws; returns null for invalid/non-object payloads. */
const safeParseJSON = (value) => {
    if (typeof value !== 'string' || value === '') {
        return null;
    }

    try {
        const parsed = JSON.parse(value);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
    } catch {
        return null;
    }
};

/** Returns a valid Date or null (guards against corrupt stored values). */
const parseDate = (value) => {
    if (!value) {
        return null;
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const daysBetweenDates = (fromDate, toDate) => {
    const from = parseDate(fromDate);
    const to = parseDate(toDate);

    if (!from || !to) {
        return null;
    }

    return (to.getTime() - from.getTime()) / MS_PER_DAY;
};

/**
 * True when `date` is further in the past than `days`.
 * Corrupt/unparsable dates are treated as "long ago" (returns true) so a
 * broken value re-shows a tutorial instead of hiding it forever.
 */
const hasMoreThanDaysPassed = (date, days) => {
    const elapsedDays = daysBetweenDates(date, getNowISO());

    if (elapsedDays === null) {
        return true;
    }

    return elapsedDays > days;
};

const createDefaultState = (userId) => ({
    userId: String(userId),
    lastSessionAt: null,
    previousSessionAt: null,
    tutorials: {},
    actions: {},
});

/** Coerces whatever is in storage into a safe, complete state object. */
const normalizeState = (rawState, userId) => {
    const base = createDefaultState(userId);

    if (!rawState) {
        return base;
    }

    return {
        ...base,
        lastSessionAt: typeof rawState.lastSessionAt === 'string' ? rawState.lastSessionAt : null,
        previousSessionAt: typeof rawState.previousSessionAt === 'string' ? rawState.previousSessionAt : null,
        tutorials: rawState.tutorials && typeof rawState.tutorials === 'object' && !Array.isArray(rawState.tutorials)
            ? { ...rawState.tutorials }
            : {},
        actions: rawState.actions && typeof rawState.actions === 'object' && !Array.isArray(rawState.actions)
            ? { ...rawState.actions }
            : {},
    };
};

const warnMissingUserOnce = () => {
    if (hasWarnedMissingUser) {
        return;
    }

    hasWarnedMissingUser = true;

    if (typeof console !== 'undefined') {
        console.warn('[useTutorialManager] Called before initUserTutorialSession(userId); tutorial checks return false.');
    }
};

/** Reads the active user's state from localStorage, or null when unavailable. */
const readState = () => {
    if (activeUserId.value === null) {
        warnMissingUserOnce();
        return null;
    }

    if (!isStorageAvailable()) {
        return null;
    }

    const rawValue = window.localStorage.getItem(getStorageKey(activeUserId.value));
    return normalizeState(safeParseJSON(rawValue), activeUserId.value);
};

/** Persists state for the active user; failures are swallowed on purpose. */
const writeState = (state) => {
    if (activeUserId.value === null || !isStorageAvailable()) {
        return false;
    }

    try {
        window.localStorage.setItem(getStorageKey(activeUserId.value), JSON.stringify(state));
        return true;
    } catch {
        return false;
    }
};

/**
 * True when the gap between the previous session and the current one is larger
 * than `repeatAfterDays`. Uses `previousSessionAt` (not `lastSessionAt`) so the
 * check keeps working after initUserTutorialSession() has refreshed the state.
 */
const wasUserInactiveBeforeSession = (repeatAfterDays) => {
    const state = readState();

    if (!state?.previousSessionAt) {
        return false;
    }

    return hasMoreThanDaysPassed(state.previousSessionAt, repeatAfterDays);
};

/* -------------------------------------------------------------------------- */
/* Public API                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Initializes the tutorial session for a user. Call once per page load, as
 * soon as the authenticated user is known. Passing a null/empty userId
 * deactivates the manager (e.g. guest pages) without breaking anything.
 *
 * Order matters: the previous `lastSessionAt` is read FIRST (to detect
 * inactivity), moved into `previousSessionAt`, and only then is
 * `lastSessionAt` refreshed with the current date.
 */
const initUserTutorialSession = (userId) => {
    if (userId === null || userId === undefined || userId === '') {
        activeUserId.value = null;
        wasInactiveOnSessionStart.value = false;
        return null;
    }

    activeUserId.value = String(userId);
    hasWarnedMissingUser = false;

    const state = readState();

    if (!state) {
        // Storage unavailable: stay initialized but without persistence.
        wasInactiveOnSessionStart.value = false;
        return null;
    }

    const previousLastSessionAt = parseDate(state.lastSessionAt) ? state.lastSessionAt : null;

    // Snapshot inactivity BEFORE overwriting lastSessionAt, otherwise a
    // long absence could never be detected.
    wasInactiveOnSessionStart.value = previousLastSessionAt
        ? hasMoreThanDaysPassed(previousLastSessionAt, DEFAULT_REPEAT_AFTER_DAYS)
        : false;

    state.previousSessionAt = previousLastSessionAt;
    state.lastSessionAt = getNowISO();

    writeState(state);

    return state;
};

/**
 * Whether a general (page-level) tutorial should be shown.
 *
 * @param {string} tutorialId
 * @param {object} [options]
 * @param {number}  [options.repeatAfterDays] Inactivity window for this tutorial.
 * @param {boolean} [options.repeatOnUserInactivity] Re-show after a long absence.
 */
const shouldShowTutorial = (tutorialId, options = {}) => {
    const {
        repeatAfterDays = DEFAULT_REPEAT_AFTER_DAYS,
        repeatOnUserInactivity = false,
    } = options;

    if (!tutorialId) {
        return false;
    }

    const state = readState();

    if (!state) {
        return false;
    }

    const tutorial = state.tutorials[tutorialId];

    // Never shown before: always show.
    if (!tutorial?.hasBeenShown) {
        return true;
    }

    if (repeatOnUserInactivity && wasUserInactiveBeforeSession(repeatAfterDays)) {
        // Re-show at most once per session: if the tutorial was already
        // re-shown after this session started, keep it hidden.
        const lastShownAt = parseDate(tutorial.lastShownAt);
        const sessionStartedAt = parseDate(state.lastSessionAt);

        if (!lastShownAt || !sessionStartedAt || lastShownAt < sessionStartedAt) {
            return true;
        }
    }

    return false;
};

/**
 * Marks a tutorial as seen. Call it right before (or right after) launching
 * Driver.js: if the user skips or closes the tour it still counts as seen.
 */
const markTutorialShown = (tutorialId) => {
    if (!tutorialId) {
        return false;
    }

    const state = readState();

    if (!state) {
        return false;
    }

    state.tutorials[tutorialId] = {
        hasBeenShown: true,
        lastShownAt: getNowISO(),
    };

    return writeState(state);
};

/**
 * Records that an action was used (right-click, button click, export, ...).
 * Refreshes `lastUsedAt`, which postpones the action's tutorial.
 */
const recordAction = (actionKey) => {
    if (!actionKey) {
        return false;
    }

    const state = readState();

    if (!state) {
        return false;
    }

    state.actions[actionKey] = {
        ...state.actions[actionKey],
        lastUsedAt: getNowISO(),
    };

    return writeState(state);
};

/**
 * Whether the tutorial tied to an action should be shown.
 *
 * Rules (X = repeatAfterDays):
 * - Never used and never shown          -> show (unless showIfNeverUsed === false).
 * - Never used, shown less than X ago   -> don't show.
 * - Never used, shown more than X ago   -> show.
 * - Used less than X days ago           -> don't show.
 * - Used more than X days ago           -> show, but rate-limited by
 *   lastTutorialShownAt so it is not repeated on every page visit.
 *
 * @param {string} actionKey
 * @param {object} [options]
 * @param {number}  [options.repeatAfterDays]
 * @param {boolean} [options.showIfNeverUsed]
 */
const shouldShowActionTutorial = (actionKey, options = {}) => {
    const {
        repeatAfterDays = DEFAULT_REPEAT_AFTER_DAYS,
        showIfNeverUsed = true,
    } = options;

    if (!actionKey) {
        return false;
    }

    const state = readState();

    if (!state) {
        return false;
    }

    const action = state.actions[actionKey] ?? {};
    const { lastUsedAt, lastTutorialShownAt } = action;

    if (!lastUsedAt) {
        if (showIfNeverUsed === false) {
            return false;
        }

        if (!lastTutorialShownAt) {
            return true;
        }

        return hasMoreThanDaysPassed(lastTutorialShownAt, repeatAfterDays);
    }

    if (hasMoreThanDaysPassed(lastUsedAt, repeatAfterDays)) {
        // The action is stale, but avoid nagging: only repeat the tutorial if
        // it also has not been shown within the same window.
        if (!lastTutorialShownAt) {
            return true;
        }

        return hasMoreThanDaysPassed(lastTutorialShownAt, repeatAfterDays);
    }

    return false;
};

/**
 * Marks the tutorial tied to an action as shown. Call it when the tour is
 * launched (skipping/closing still counts as seen). Preserves `lastUsedAt`.
 */
const markActionTutorialShown = (actionKey) => {
    if (!actionKey) {
        return false;
    }

    const state = readState();

    if (!state) {
        return false;
    }

    state.actions[actionKey] = {
        ...state.actions[actionKey],
        lastTutorialShownAt: getNowISO(),
    };

    return writeState(state);
};

/** Removes the stored state of a single tutorial. */
const resetTutorial = (tutorialId) => {
    const state = readState();

    if (!state || !tutorialId) {
        return false;
    }

    delete state.tutorials[tutorialId];

    return writeState(state);
};

/** Removes the stored state of a single action. */
const resetAction = (actionKey) => {
    const state = readState();

    if (!state || !actionKey) {
        return false;
    }

    delete state.actions[actionKey];

    return writeState(state);
};

/** Wipes all tutorial data for the current user. */
const resetAllTutorialData = () => {
    if (activeUserId.value === null || !isStorageAvailable()) {
        return false;
    }

    try {
        window.localStorage.removeItem(getStorageKey(activeUserId.value));
        return true;
    } catch {
        return false;
    }
};

/**
 * Removes the tutorial state of every user stored by this origin, for tenants
 * whose profile disables tutorials (config/browser_services.php). Never writes.
 */
export const forgetStoredTutorialSessions = () => {
    activeUserId.value = null;
    wasInactiveOnSessionStart.value = false;

    try {
        const storage = window.localStorage;
        const keys = [];
        for (let index = 0; index < storage.length; index += 1) {
            const key = storage.key(index);
            if (key?.startsWith(`${STORAGE_KEY_PREFIX}:`)) keys.push(key);
        }
        keys.forEach((key) => storage.removeItem(key));
        return keys.length;
    } catch {
        return 0;
    }
};

/** Current persisted state (plus session flags), useful for debugging. */
const getTutorialManagerState = () => {
    const state = readState();

    if (!state) {
        return null;
    }

    return {
        ...state,
        wasInactiveOnSessionStart: wasInactiveOnSessionStart.value,
    };
};

export function useTutorialManager() {
    return {
        DEFAULT_REPEAT_AFTER_DAYS,
        activeUserId,
        wasInactiveOnSessionStart,
        initUserTutorialSession,
        shouldShowTutorial,
        markTutorialShown,
        recordAction,
        shouldShowActionTutorial,
        markActionTutorialShown,
        resetTutorial,
        resetAction,
        resetAllTutorialData,
        getTutorialManagerState,
    };
}

export default useTutorialManager;
